import type { AppointmentCardProps } from '@/types'
import { cn } from '@/utils/cn'

// TODO: not currently implemented, this was just automatically scaffolded demo code

/**
 * AppointmentCard displays appointment details with action buttons.
 * Supports different appointment statuses and types.
 */
export function AppointmentCard({ data, onAction }: AppointmentCardProps) {
  const { id, doctorName, specialty, date, time, location, status } = data

  const handleReschedule = () => {
    onAction?.('reschedule-appointment', { appointmentId: id })
  }

  const handleCancel = () => {
    onAction?.('cancel-appointment', { appointmentId: id })
  }

  // Status badge styles
  const statusStyles = {
    scheduled: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    completed: 'bg-gray-100 text-gray-800',
  }

  // Status labels for screen readers
  const statusLabels = {
    scheduled: 'Appointment scheduled',
    confirmed: 'Appointment confirmed',
    cancelled: 'Appointment cancelled',
    completed: 'Appointment completed',
  }

  const isActive = status === 'scheduled' || status === 'confirmed'

  return (
    <article
      className={cn(
        'rounded-lg border bg-white p-4 shadow-sm',
        status === 'cancelled' ? 'border-gray-200 opacity-75' : 'border-gray-200'
      )}
      aria-labelledby={`appointment-${id}`}
    >
      {/* Header with status */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3
            id={`appointment-${id}`}
            className={cn(
              'font-semibold',
              status === 'cancelled' ? 'text-gray-500 line-through' : 'text-gray-900'
            )}
          >
            {doctorName}
          </h3>
          <p className="text-sm text-gray-600">{specialty}</p>
        </div>

        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5',
            'text-xs font-medium',
            statusStyles[status]
          )}
          role="status"
          aria-label={statusLabels[status]}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      {/* Appointment details */}
      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <dt className="sr-only">Date and time</dt>
          <dd className="flex items-center gap-2 text-gray-700">
            <span aria-hidden="true" className="text-gray-400">
              {'\u{1F4C5}'}
            </span>
            {date} at {time}
          </dd>
        </div>

        <div className="flex items-center gap-2">
          <dt className="sr-only">Location</dt>
          <dd className="flex items-center gap-2 text-gray-700">
            <span aria-hidden="true" className="text-gray-400">
              {'\u{1F4CD}'}
            </span>
            {location}
          </dd>
        </div>
      </dl>

      {/* Action buttons */}
      {isActive && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleReschedule}
            className={cn(
              'flex-1 rounded-md border border-gray-300 px-4 py-2',
              'text-sm font-medium text-gray-700',
              'hover:bg-gray-50 focus:outline-none focus:ring-2',
              'focus:ring-primary-500 focus:ring-offset-2',
              'transition-colors'
            )}
          >
            Reschedule
          </button>

          <button
            type="button"
            onClick={handleCancel}
            className={cn(
              'rounded-md border border-gray-300 px-4 py-2',
              'text-sm font-medium text-error-600',
              'hover:bg-error-50 focus:outline-none focus:ring-2',
              'focus:ring-error-500 focus:ring-offset-2',
              'transition-colors'
            )}
          >
            Cancel
          </button>
        </div>
      )}
    </article>
  )
}
